import { useState, useRef, useCallback } from "react";
import { useFlowsTemplates } from "../../../../stores/flowsTemplatesStore";

export interface UseTemplateFileUploadProps {
  templateId: string;
  onClose: () => void;
}

export interface UseTemplateFileUploadReturn {
  selectedFile: File | null;
  loading: boolean;
  dragOver: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  setSelectedFile: (file: File | null) => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDrop: (event: React.DragEvent) => void;
  handleDragOver: (event: React.DragEvent) => void;
  handleDragLeave: () => void;
  handleUpload: () => Promise<void>;
  handleClose: () => void;
  formatFileSize: (bytes: number) => string;
  isSubmitDisabled: boolean;
}

export function useTemplateFileUpload({
  templateId,
  onClose,
}: UseTemplateFileUploadProps): UseTemplateFileUploadReturn {
  const { uploadTemplateFile } = useFlowsTemplates();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setLoading(true);
    await uploadTemplateFile(templateId, selectedFile);
    setLoading(false);
    setSelectedFile(null);
    onClose();
  }, [selectedFile, templateId, uploadTemplateFile, onClose]);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    onClose();
  }, [onClose]);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  const isSubmitDisabled = !selectedFile || loading;

  return {
    selectedFile,
    loading,
    dragOver,
    fileInputRef,
    setSelectedFile,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleUpload,
    handleClose,
    formatFileSize,
    isSubmitDisabled,
  };
}
